<script setup lang="ts">
import { ArrowLeft, Check, Clipboard, Copy, Download, Network, RotateCcw, ShieldCheck, Split } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { formatInteger, planCidr, planSummary, previewSubnets, subnetPreviewCsv } from "../lib/cidr";

const input = ref("192.168.10.42/24");
const splitPrefix = ref(26);
const copyState = ref<"idle" | "done" | "error">("idle");

const analysis = computed(() => {
  try {
    return { plan: planCidr(input.value), error: "" };
  } catch (error) {
    return { plan: null, error: error instanceof Error ? error.message : "无法解析此 CIDR。" };
  }
});

const split = computed(() => {
  if (!analysis.value.plan) return null;
  try {
    return previewSubnets(analysis.value.plan, splitPrefix.value);
  } catch {
    return null;
  }
});

watch(() => analysis.value.plan?.normalizedCidr, () => {
  const plan = analysis.value.plan;
  if (plan) splitPrefix.value = Math.min(plan.prefix + 2, plan.bits);
  copyState.value = "idle";
}, { immediate: true });

function useExample(value: string) {
  input.value = value;
}

function reset() {
  input.value = "192.168.10.42/24";
  copyState.value = "idle";
}

async function copyResult() {
  const plan = analysis.value.plan;
  if (!plan) return;
  try {
    await navigator.clipboard.writeText(planSummary(plan));
    copyState.value = "done";
  } catch {
    copyState.value = "error";
  }
}

function downloadCsv() {
  if (!split.value || !analysis.value.plan) return;
  const blob = new Blob([subnetPreviewCsv(split.value)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${analysis.value.plan.normalizedCidr.replaceAll("/", "-")}-subnets.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
</script>

<template>
  <header class="tool-head">
    <div class="page-width tool-head-inner">
      <div>
        <RouterLink class="tool-back" to="/tools"><ArrowLeft :size="15" />工具与资源</RouterLink>
        <span class="tool-kicker"><Network :size="15" />网络规划</span>
        <h1>CIDR 子网规划器</h1>
        <p>把一个 IPv4 或 IPv6 前缀展开为准确边界，并按新前缀预览子网。</p>
      </div>
      <div class="local-compute"><ShieldCheck :size="18" /><span><strong>浏览器本地计算</strong><small>输入不会上传</small></span></div>
    </div>
  </header>

  <section class="tool-workbench">
    <div class="page-width cidr-layout">
      <aside class="cidr-controls" aria-label="CIDR 输入">
        <div class="control-heading"><span>输入</span><button type="button" title="重置" aria-label="重置 CIDR" @click="reset"><RotateCcw :size="16" /></button></div>
        <div class="example-switch" aria-label="CIDR 示例">
          <button type="button" :class="{ active: analysis.plan?.family === 'ipv4' }" @click="useExample('192.168.10.42/24')">IPv4</button>
          <button type="button" :class="{ active: analysis.plan?.family === 'ipv6' }" @click="useExample('2001:db8:12:3400::18/64')">IPv6</button>
        </div>
        <label class="cidr-input-label" :class="{ invalid: analysis.error }">
          <span>地址与前缀</span>
          <input v-model.trim="input" type="text" inputmode="text" spellcheck="false" autocomplete="off" aria-describedby="cidr-input-message" />
        </label>
        <p id="cidr-input-message" class="input-message" :class="{ error: analysis.error }">
          {{ analysis.error || (analysis.plan?.isAligned ? "已是对齐的网络前缀。" : `主机地址将归一为 ${analysis.plan?.normalizedCidr}。`) }}
        </p>

        <div v-if="analysis.plan" class="split-control">
          <div><span>拆分前缀</span><strong>/{{ splitPrefix }}</strong></div>
          <input v-model.number="splitPrefix" type="range" :min="analysis.plan.prefix" :max="analysis.plan.bits" step="1" aria-label="拆分前缀长度" />
          <div class="split-bounds"><small>/{{ analysis.plan.prefix }}</small><small>/{{ analysis.plan.bits }}</small></div>
        </div>

        <div class="control-actions">
          <button class="primary-button" type="button" :disabled="!analysis.plan" @click="copyResult">
            <Check v-if="copyState === 'done'" :size="17" /><Copy v-else :size="17" />
            {{ copyState === "done" ? "已复制" : copyState === "error" ? "复制失败" : "复制结果" }}
          </button>
          <button class="secondary-button" type="button" :disabled="!split" @click="downloadCsv"><Download :size="17" />下载 CSV</button>
        </div>
      </aside>

      <div class="cidr-result" aria-live="polite">
        <div v-if="analysis.plan" class="result-content">
          <div class="result-title">
            <div><span>{{ analysis.plan.family === "ipv4" ? "IPv4 网络" : "IPv6 前缀" }}</span><h2>{{ analysis.plan.normalizedCidr }}</h2></div>
            <Clipboard :size="22" aria-hidden="true" />
          </div>

          <dl class="cidr-metrics" :class="{ 'two-up': analysis.plan.usableAddresses === null }">
            <div><dt>前缀长度</dt><dd>/{{ analysis.plan.prefix }}</dd></div>
            <div><dt>地址总数</dt><dd>{{ formatInteger(analysis.plan.totalAddresses) }}</dd></div>
            <div v-if="analysis.plan.usableAddresses !== null"><dt>可用地址</dt><dd>{{ formatInteger(analysis.plan.usableAddresses) }}</dd></div>
          </dl>

          <dl class="address-facts">
            <div><dt>网络地址</dt><dd>{{ analysis.plan.networkAddress }}</dd></div>
            <div><dt>末地址</dt><dd>{{ analysis.plan.lastAddress }}</dd></div>
            <div v-if="analysis.plan.netmask"><dt>子网掩码</dt><dd>{{ analysis.plan.netmask }}</dd></div>
            <div v-if="analysis.plan.wildcardMask"><dt>通配掩码</dt><dd>{{ analysis.plan.wildcardMask }}</dd></div>
            <div v-if="analysis.plan.broadcastAddress"><dt>定向广播</dt><dd>{{ analysis.plan.broadcastAddress }}</dd></div>
            <div v-if="analysis.plan.firstUsable"><dt>可用范围</dt><dd>{{ analysis.plan.firstUsable }} <span>至</span> {{ analysis.plan.lastUsable }}</dd></div>
          </dl>
          <p class="cidr-note">{{ analysis.plan.note }}</p>
        </div>
        <div v-else class="result-empty"><Network :size="34" /><strong>等待有效 CIDR</strong><p>更正左侧输入后，边界与拆分结果会立即更新。</p></div>
      </div>
    </div>
  </section>

  <section v-if="analysis.plan && split" class="subnet-band">
    <div class="page-width subnet-layout">
      <div class="subnet-intro">
        <Split :size="22" />
        <span>子网预览</span>
        <h2>{{ analysis.plan.normalizedCidr }} 拆为 /{{ split.newPrefix }}</h2>
        <p>共 {{ formatInteger(split.totalSubnets) }} 个子网，每个包含 {{ formatInteger(split.rows[0]?.addresses ?? 0n) }} 个地址。</p>
        <small v-if="split.truncated">为保持页面流畅，仅显示前 {{ split.shownSubnets }} 个；CSV 同样导出当前预览。</small>
      </div>
      <div class="subnet-table-wrap">
        <table class="subnet-table">
          <thead><tr><th>#</th><th>CIDR</th><th>首地址</th><th>末地址</th></tr></thead>
          <tbody>
            <tr v-for="row in split.rows" :key="row.index">
              <td>{{ row.index }}</td><td>{{ row.cidr }}</td><td>{{ row.firstAddress }}</td><td>{{ row.lastAddress }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <section class="tool-sources">
    <div class="page-width">
      <span>计算依据</span>
      <p>前缀和地址边界遵循 IETF 定义，解析使用经过维护的开源库。</p>
      <div>
        <a href="https://www.rfc-editor.org/rfc/rfc4632.html" target="_blank" rel="noreferrer">RFC 4632 · IPv4 CIDR</a>
        <a href="https://www.rfc-editor.org/rfc/rfc4291.html" target="_blank" rel="noreferrer">RFC 4291 · IPv6 地址架构</a>
        <a href="https://www.rfc-editor.org/rfc/rfc3021.html" target="_blank" rel="noreferrer">RFC 3021 · IPv4 /31</a>
        <a href="https://github.com/whitequark/ipaddr.js/" target="_blank" rel="noreferrer">ipaddr.js 2.5.0</a>
      </div>
    </div>
  </section>
</template>
